// F9.2-X · System Truth Contract — State Sanity Assertion.
//
// Função `assertEvmIntegrity` aplicada pela State Access Layer (SAL) a TODA
// linha de KPI retornada de `emp_vw_indicadores_blindados`. Fail-closed: em
// caso de violação, lança `EvmIntegrityError` e interrompe a query — sem
// fallback silencioso.
//
// Regras (ver docs/architecture/F92X_system_truth_contract.md §3):
//   SI-1 _evm_source === 'blindados'
//   SI-2 pv, ev, ac finitos e ≥ 0
//   SI-3 spi, cpi finitos quando definidos
//   SI-4 percentual_* em [0, 200]
//   SI-5 baseline_bac ≥ 0 quando definido
//   SI-6 eac finito quando definido

export class EvmIntegrityError extends Error {
  readonly code: string;
  readonly field: string;
  readonly value: unknown;
  readonly contratoId: string | undefined;
  readonly periodo: string | undefined;

  constructor(args: {
    code: string;
    field: string;
    value: unknown;
    contratoId?: string;
    periodo?: string;
    message?: string;
  }) {
    super(
      args.message ??
        `[EVM-INTEGRITY ${args.code}] Violação em '${args.field}'=${String(
          args.value,
        )} (contrato=${args.contratoId ?? '?'}, periodo=${args.periodo ?? '?'})`,
    );
    this.name = 'EvmIntegrityError';
    this.code = args.code;
    this.field = args.field;
    this.value = args.value;
    this.contratoId = args.contratoId;
    this.periodo = args.periodo;
  }
}

const PERCENT_MAX = 200; // Tolera superação documentada (modelo triplo-controle).

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function assertNonNegativeFinite(
  payload: Record<string, unknown>,
  field: string,
  code: string,
) {
  const v = payload[field];
  if (!isFiniteNumber(v) || v < 0) {
    throw new EvmIntegrityError({
      code,
      field,
      value: v,
      contratoId: payload.contrato_id as string | undefined,
      periodo: payload.periodo_referencia as string | undefined,
    });
  }
}

function assertOptionalFinite(
  payload: Record<string, unknown>,
  field: string,
  code: string,
) {
  const v = payload[field];
  if (v === null || v === undefined) return;
  if (!isFiniteNumber(v)) {
    throw new EvmIntegrityError({
      code,
      field,
      value: v,
      contratoId: payload.contrato_id as string | undefined,
      periodo: payload.periodo_referencia as string | undefined,
    });
  }
}

function assertPercent(
  payload: Record<string, unknown>,
  field: string,
  code: string,
) {
  const v = payload[field];
  if (v === null || v === undefined) return;
  if (!isFiniteNumber(v) || v < 0 || v > PERCENT_MAX) {
    throw new EvmIntegrityError({
      code,
      field,
      value: v,
      contratoId: payload.contrato_id as string | undefined,
      periodo: payload.periodo_referencia as string | undefined,
    });
  }
}

/**
 * Aplica todas as regras SI-1..SI-6 ao payload de KPI.
 * Lança `EvmIntegrityError` na primeira violação encontrada.
 */
export function assertEvmIntegrity(
  payload: Record<string, unknown>,
): void {
  // SI-1 — fonte travada
  if (payload._evm_source !== 'blindados') {
    throw new EvmIntegrityError({
      code: 'SI-1',
      field: '_evm_source',
      value: payload._evm_source,
      contratoId: payload.contrato_id as string | undefined,
      periodo: payload.periodo_referencia as string | undefined,
      message:
        '[EVM-INTEGRITY SI-1] Fonte de KPI fora do contrato — esperado "blindados".',
    });
  }

  // SI-2 — PV/EV/AC obrigatórios
  assertNonNegativeFinite(payload, 'pv', 'SI-2');
  assertNonNegativeFinite(payload, 'ev', 'SI-2');
  assertNonNegativeFinite(payload, 'ac', 'SI-2');

  // SI-3 — índices opcionais finitos
  assertOptionalFinite(payload, 'spi', 'SI-3');
  assertOptionalFinite(payload, 'cpi', 'SI-3');

  // SI-4 — percentuais
  assertPercent(payload, 'percentual_fisico_previsto', 'SI-4');
  assertPercent(payload, 'percentual_fisico_realizado', 'SI-4');
  assertPercent(payload, 'percentual_financeiro_previsto', 'SI-4');
  assertPercent(payload, 'percentual_financeiro_realizado', 'SI-4');

  // SI-5 — baseline_bac
  const bac = payload.baseline_bac;
  if (bac !== null && bac !== undefined) {
    if (!isFiniteNumber(bac) || bac < 0) {
      throw new EvmIntegrityError({
        code: 'SI-5',
        field: 'baseline_bac',
        value: bac,
        contratoId: payload.contrato_id as string | undefined,
        periodo: payload.periodo_referencia as string | undefined,
      });
    }
  }

  // SI-6 — EAC finito quando definido
  assertOptionalFinite(payload, 'eac', 'SI-6');
  assertOptionalFinite(payload, 'etc', 'SI-6');
  assertOptionalFinite(payload, 'vac', 'SI-6');
}
